import { useGetAgentCenterAnalytics } from 'services/hooks/queries';
import { formatMillisecondsAsDayHourMinSec } from 'utils/dateformat.utils';
import { AgentActivityChart } from './components/AgentActivityChart';
import { AgentAnalyticsItem, formatValue } from './components/AgentAnalyticsItem';
import { Hourglass, Timer } from 'lucide-react';

import styles from './agentAnalytics.module.scss';

const noOperationalData = 'No operational data';

function transfersCompletedTrendPct(today: number, yesterday: number): number {
    if (yesterday === 0) {
        return today > 0 ? today * 100 : 0;
    }
    return ((today - yesterday) / yesterday) * 100;
}

export const AgentAnalytics = () => {
    const {
        agentCenterAnalyticsData: { agentPerformance, operational, activityByHour },
        isAgentCenterAnalyticsLoading,
    } = useGetAgentCenterAnalytics();

    const {
        transferResponseRate,
        transferConversionRate,
        transfersCompletedToday,
        transferOutcomesTotalToday,
        transfersCompletedYesterday,
    } = agentPerformance;
    const { callbacksHandled, callbackResponseRate, callbackConversionRate } = agentPerformance;

    const { timeOnlineTodaySeconds, waitingTimeSeconds } = operational;

    return (
        <div className='flex flex-col gap-xl'>
            <div className={styles.agentAnalyticsGrid}>
                <div className={styles.agentOperationalAnalytics}>
                    {/* Operational Analytics */}
                    <div className={`${styles.agentAnalyticsItem} ${styles.operationalCardActiveTime}`}>
                        <div className={styles.operationalCardIcon}>
                            <Hourglass color='var(--primary-purple)' size={20} />
                        </div>
                        <h5>Active time today</h5>

                        <p className='text-3xl font-bold text-primary'>
                            {timeOnlineTodaySeconds
                                ? formatMillisecondsAsDayHourMinSec(timeOnlineTodaySeconds * 1000)
                                : noOperationalData}
                        </p>
                    </div>

                    <div className={`${styles.agentAnalyticsItem} ${styles.operationalCardWaiting}`}>
                        <div className={styles.operationalCardIcon}>
                            <Timer color='var(--warning-color)' size={20} />
                        </div>
                        <h5>Waiting since last transfer</h5>
                        <p className='text-3xl font-bold text-warning'>
                            {waitingTimeSeconds
                                ? formatMillisecondsAsDayHourMinSec(waitingTimeSeconds * 1000)
                                : noOperationalData}
                        </p>
                    </div>
                </div>
                <AgentActivityChart activityByHour={activityByHour} isLoading={isAgentCenterAnalyticsLoading} />
            </div>
            <div className={styles.agentAnalyticsGrid}>
                {/* Performance Analytics */}
                <AgentAnalyticsItem
                    title='Transferred calls'
                    value={transfersCompletedToday}
                    label={`of ${transferOutcomesTotalToday} assigned`}
                    trend={transfersCompletedTrendPct(transfersCompletedToday, transfersCompletedYesterday)}
                    isLoading={isAgentCenterAnalyticsLoading}
                />

                <AgentAnalyticsItem
                    title='Transfer response'
                    value={transferResponseRate?.value}
                    trend={transferResponseRate?.trendVsYesterdayPct}
                    isLoading={isAgentCenterAnalyticsLoading}
                    isPercentage
                />

                <AgentAnalyticsItem
                    title='Transfer conversion'
                    value={transferConversionRate?.value}
                    trend={transferConversionRate?.trendVsYesterdayPct}
                    isLoading={isAgentCenterAnalyticsLoading}
                    isPercentage
                />

                <AgentAnalyticsItem
                    title='Callbacks taken'
                    label={`${callbacksHandled} total assigned`}
                    value={callbackResponseRate?.value}
                    trend={callbackResponseRate?.trendVsYesterdayPct}
                    isLoading={isAgentCenterAnalyticsLoading}
                    isPercentage
                />

                <AgentAnalyticsItem
                    title='Callback conversion'
                    label={`${formatValue(callbacksHandled / (timeOnlineTodaySeconds / 3600), true)} per hour online`}
                    value={callbackConversionRate?.value}
                    trend={callbackConversionRate?.trendVsYesterdayPct}
                    isLoading={isAgentCenterAnalyticsLoading}
                    isPercentage
                />
            </div>
        </div>
    );
};
